import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FolderKanban, Plus, Search, X } from 'lucide-react';
import { Modal } from '../ui/Modal';
import OsScroll from '../ui/OsScroll';
import { useScopeLabel } from './GraphScopePicker';

/**
 * "Add to project" for users with many projects (owner, 13.09.2026): the
 * right-click menu shows one "Add to project…" entry instead of a line per
 * project, and this modal lists every project with a filter. Each row
 * toggles membership — the item can belong to several projects.
 *
 * `projects` is already the list this item may use (GraphView.projectsFor):
 * personal projects, and its own team's projects where the user is not a
 * viewer. A team project says its team next to its name.
 */
export default function ProjectPickerModal({ node, projects, isMember, onToggle, onClose }) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const scopeLabel = useScopeLabel();

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle ? projects.filter((p) => p.name.toLowerCase().includes(needle)) : projects;
    // Projects the item is already in first, then A–Z.
    return [...list].sort((a, b) => Number(isMember(b.id)) - Number(isMember(a.id)) || a.name.localeCompare(b.name));
  }, [projects, q, isMember]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && filtered[active]) {
      e.preventDefault();
      onToggle(filtered[active].id);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={t('graph.projectPicker.title', { name: node.label })} customIcon={<FolderKanban size={18} />}>
      <div className="pp" data-testid="project-picker">
        <label className="pp-search">
          <Search size={14} aria-hidden />
          <input
            autoFocus
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={t('graph.projectPicker.filter')}
            aria-label={t('graph.projectPicker.filter')}
          />
          {q && (
            <button type="button" className="pp-clear" onClick={() => setQ('')} aria-label={t('common.close')}>
              <X size={12} />
            </button>
          )}
        </label>
        <OsScroll className="pp-list-scroll">
          <ul className="pp-list" role="listbox" aria-label={t('graph.projects')}>
            {filtered.length === 0 && <li className="pp-empty">{t('projects.noResults')}</li>}
            {filtered.map((p, i) => {
              const member = isMember(p.id);
              return (
                <li key={p.id} role="option" aria-selected={member}>
                  <button
                    type="button"
                    className={`pp-row${i === active ? ' pp-row--active' : ''}${member ? ' pp-row--member' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => onToggle(p.id)}
                    aria-label={member ? t('graph.menu.removeFrom', { name: p.name }) : t('graph.menu.addTo', { name: p.name })}
                  >
                    <span className="pp-swatch" style={{ background: p.color }} aria-hidden />
                    <span className="pp-name">
                      {p.name}
                      {scopeLabel(p) && <span className="pp-scope"> · {scopeLabel(p)}</span>}
                    </span>
                    <span className="pp-state" aria-hidden>
                      {member ? <Check size={14} /> : <Plus size={14} />}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </OsScroll>
      </div>
    </Modal>
  );
}
